import { AccountTypes, BusinessDeviceType } from '@am3nd-org/pointcraft-types';

const DEVICE_FIREBASE_UID_PREFIX = 'BUSINESS_DEVICE:';

export type JwtClaims = Record<string, unknown>;

export type BusinessDeviceAuthTokenStatus = 'v3' | 'legacy' | 'invalid';

export type ExpectedBusinessDeviceIdentity = {
  businessId: string;
  businessDeviceId: string;
  deviceType: BusinessDeviceType;
};

export type ValidatedBusinessDeviceClaims = ExpectedBusinessDeviceIdentity & {
  firebaseUid: string;
};

export class BusinessDeviceIdentityError extends Error {
  readonly errorCode = 'TOKEN_CLAIMS_MISMATCH';

  constructor(message = 'Auth V3 token identity does not match the expected device') {
    super(message);
    this.name = 'BusinessDeviceIdentityError';
  }
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value);

const isBusinessDeviceType = (value: unknown): value is BusinessDeviceType =>
  typeof value === 'string' && (Object.values(BusinessDeviceType) as string[]).includes(value);

const getStringClaim = (claims: JwtClaims | null, key: string): string | undefined => {
  const value = claims?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const decodeBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return atob(padded);
};

export const decodeJwtClaims = (token: string): JwtClaims | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const json = decodeURIComponent(
      decodeBase64Url(payload)
        .split('')
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );

    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
};

export const getTokenFirebaseUid = (claims: JwtClaims | null): string | undefined =>
  getStringClaim(claims, 'user_id') ?? getStringClaim(claims, 'sub');

export const getValidatedBusinessDeviceClaims = (
  claims: JwtClaims | null,
  expected?: ExpectedBusinessDeviceIdentity | null
): ValidatedBusinessDeviceClaims | null => {
  const firebaseUid = getTokenFirebaseUid(claims);
  const businessId = claims?.businessId;
  const businessDeviceId = claims?.businessDeviceId;
  const deviceType = claims?.deviceType;

  if (
    claims?.accountType !== AccountTypes.BUSINESS_DEVICE ||
    !isUuid(businessId) ||
    !isUuid(businessDeviceId) ||
    !isBusinessDeviceType(deviceType) ||
    firebaseUid !== `${DEVICE_FIREBASE_UID_PREFIX}${businessDeviceId}`
  ) {
    return null;
  }

  if (
    expected &&
    (expected.businessId !== businessId ||
      expected.businessDeviceId !== businessDeviceId ||
      expected.deviceType !== deviceType)
  ) {
    return null;
  }

  return { businessId, businessDeviceId, deviceType, firebaseUid };
};

export const hasBusinessDeviceV3Claims = (
  claims: JwtClaims | null,
  expected?: ExpectedBusinessDeviceIdentity | null
): boolean => getValidatedBusinessDeviceClaims(claims, expected) !== null;

export const assertBusinessDeviceV3Claims = (
  claims: JwtClaims | null,
  expected: ExpectedBusinessDeviceIdentity
): ValidatedBusinessDeviceClaims => {
  const validated = getValidatedBusinessDeviceClaims(claims, expected);
  if (!validated) throw new BusinessDeviceIdentityError();
  return validated;
};

export const getBusinessDeviceAuthTokenStatus = (
  token: string | null | undefined,
  expected?: ExpectedBusinessDeviceIdentity | null
): BusinessDeviceAuthTokenStatus => {
  if (!token) return 'invalid';

  const claims = decodeJwtClaims(token);
  if (!claims) return 'invalid';

  if (claims.accountType === AccountTypes.BUSINESS_DEVICE) {
    return hasBusinessDeviceV3Claims(claims, expected) ? 'v3' : 'invalid';
  }

  return 'legacy';
};
